/**
 * correctionWatch — la correction attendue qui arrive quand l'élève n'est plus sur l'attente : il l'a
 * quittée avant elle (« Continuer sans attendre », une autre activité affichée par le tuteur ou ouverte,
 * le délai dépassé puis « Activité suivante »), ou ne l'a pas eue : en live en Dialogue, la copie ne
 * fait pas attendre sa correction (25/10), qui s'annonce de même quand elle arrive (26/10). Décidé par
 * Wajih le 20/10 : on le signale, une fois.
 *
 * Un réducteur pur : une attente quittée se surveille (`LEFT`), un relevé dit où en est chaque correction
 * (`POLLED`) ; prête, elle s'annonce, et une seule fois (`DISMISSED`, ou `SEEN` quand l'élève la regarde
 * déjà). Une correction qui ne viendra pas (échec, quota, pas de correction) cesse d'être surveillée sans
 * un mot : l'activité finie le dit. Au-delà de `CORRECTION_WATCH_MS`, la surveillance s'arrête aussi ;
 * la correction se lit alors dans l'activité finie, comme avant.
 */
import { deriveCorrectionFlow } from "./correctionFlow"

/** Combien de temps une attente quittée se surveille encore (proposé). */
export const CORRECTION_WATCH_MS = 3 * 60 * 1000

export type WatchedCorrection = { id: number; title: string }

export type CorrectionWatchState = {
    /** Les corrections encore en route, avec l'heure où l'on cesse de les relever. */
    watching: (WatchedCorrection & { until: number })[]
    /** Les corrections prêtes, pas encore annoncées : la première se montre. */
    ready: WatchedCorrection[]
    /** Ce qui ne s'annoncera plus : annoncé, ou vu par l'élève. */
    settled: number[]
}

/** Une ligne du relevé (`GET api/activities?activity_ids=…`). */
export type PolledCorrection = {
    id?: number | null
    status?: string | null
    correction_status?: string | null
}

export type CorrectionWatchEvent =
    | { type: "LEFT"; correction: WatchedCorrection; at: number }
    | { type: "POLLED"; rows: PolledCorrection[]; at: number }
    /** L'heure passe : ce qui a dépassé son délai cesse d'être relevé, même quand le relevé échoue. */
    | { type: "TICK"; at: number }
    | { type: "SEEN"; id: number }
    | { type: "DISMISSED"; id: number }

export const initialCorrectionWatch: CorrectionWatchState = { watching: [], ready: [], settled: [] }

const without = <T extends { id: number }>(list: T[], id: number) => list.filter((item) => item.id !== id)

export function correctionWatchReducer(state: CorrectionWatchState, event: CorrectionWatchEvent): CorrectionWatchState {
    switch (event.type) {
        case "LEFT": {
            const { id } = event.correction
            const known =
                state.settled.includes(id) ||
                state.ready.some((item) => item.id === id) ||
                state.watching.some((item) => item.id === id)
            if (known) return state
            return {
                ...state,
                watching: [...state.watching, { ...event.correction, until: event.at + CORRECTION_WATCH_MS }],
            }
        }
        case "POLLED": {
            const watching: CorrectionWatchState["watching"] = []
            const ready = [...state.ready]
            for (const item of state.watching) {
                const row = event.rows.find((candidate) => candidate?.id === item.id)
                const flow =
                    row != null
                        ? deriveCorrectionFlow({ reviewStatus: row.status, correctionStatus: row.correction_status })
                        : null
                if (flow?.completed) ready.push({ id: item.id, title: item.title })
                else if ((flow == null || flow.awaiting) && item.until > event.at) watching.push(item)
            }
            if (watching.length === state.watching.length && ready.length === state.ready.length) return state
            return { ...state, watching, ready }
        }
        case "TICK": {
            const watching = state.watching.filter((item) => item.until > event.at)
            return watching.length === state.watching.length ? state : { ...state, watching }
        }
        case "SEEN":
        case "DISMISSED": {
            // Seule une correction surveillée se règle : l'activité ouverte, encore à faire, garde son
            // annonce pour plus tard.
            const watched =
                state.watching.some((item) => item.id === event.id) || state.ready.some((item) => item.id === event.id)
            if (!watched) return state
            return {
                watching: without(state.watching, event.id),
                ready: without(state.ready, event.id),
                settled: [...state.settled, event.id],
            }
        }
        default:
            return state
    }
}

/** La correction à annoncer maintenant, ou rien. */
export const correctionNotice = (state: CorrectionWatchState): WatchedCorrection | null => state.ready[0] ?? null

/** Les activités à relever, quand il en reste. */
export const watchedIds = (state: CorrectionWatchState): number[] => state.watching.map((item) => item.id)
